import React, { useState, useMemo, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { Sidebar } from './Sidebar'
import { CriticalReminderBanner } from '../reminders/CriticalReminderBanner'
import { Header } from './Header'
import { MobileLayout } from './MobileLayout'
import { useThemeSync } from '../../hooks/useThemeSync'
import { useFirstRun } from '../../hooks/useFirstRun'
import { useTheme } from '../providers/ThemeProvider'
import { FeedbackProvider } from '../feedback/FeedbackContext'
import { HortonChatProvider } from '../ai/HortonChatContext'
import { FloatingHortonButton } from '../ai/FloatingHortonButton'

// Helper to generate paper background styles from theme config
function getPaperBackgroundStyle(
  config: { paper: { type: string; solidColor: string; gradientFrom: string; gradientTo: string; gradientDirection: string; textureId: string; patternId: string; patternOpacity: number } } | null,
): React.CSSProperties {
  if (!config?.paper) {
    return { backgroundColor: 'var(--paper-cream)' }
  }

  const { paper } = config
  const baseColor = paper.solidColor || 'var(--paper-cream)'

  // Build background layers
  const backgrounds: string[] = []
  
  // Pattern layer
  if (paper.patternId && paper.patternId !== 'none') {
    const opacity = paper.patternOpacity || 0.15
    const inkFadedLine = `color-mix(in srgb, var(--ink-faded) ${Math.round(opacity * 100)}%, transparent)`
    
    switch (paper.patternId) {
      case 'ruled':
        backgrounds.push(`repeating-linear-gradient(transparent, transparent 27px, ${inkFadedLine} 27px, ${inkFadedLine} 28px)`)
        break
      case 'wide-ruled':
        backgrounds.push(`repeating-linear-gradient(transparent, transparent 35px, ${inkFadedLine} 35px, ${inkFadedLine} 36px)`)
        break
      case 'narrow-ruled':
        backgrounds.push(`repeating-linear-gradient(transparent, transparent 19px, ${inkFadedLine} 19px, ${inkFadedLine} 20px)`)
        break
      case 'margin':
        backgrounds.push(`linear-gradient(90deg, transparent 59px, #C45C4A40 59px, #C45C4A40 61px, transparent 61px), repeating-linear-gradient(transparent, transparent 27px, ${inkFadedLine} 27px, ${inkFadedLine} 28px)`)
        break
      case 'dot-grid':
        backgrounds.push(`radial-gradient(circle, ${inkFadedLine} 1px, transparent 1px)`)
        break
      case 'graph':
        backgrounds.push(`linear-gradient(${inkFadedLine} 1px, transparent 1px), linear-gradient(90deg, ${inkFadedLine} 1px, transparent 1px)`)
        break
    }
  }
  
  // Texture layer
  if (paper.textureId && paper.textureId !== 'plain') {
    switch (paper.textureId) {
      case 'aged':
        backgrounds.push(`radial-gradient(ellipse at 20% 30%, rgba(139, 115, 85, 0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(139, 115, 85, 0.06) 0%, transparent 40%)`)
        break
      case 'parchment':
        backgrounds.push(`radial-gradient(ellipse at 0% 0%, rgba(139, 115, 85, 0.1) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(139, 115, 85, 0.08) 0%, transparent 50%)`)
        break
      case 'watercolor':
        backgrounds.push(`radial-gradient(ellipse at 10% 20%, rgba(123, 104, 166, 0.08) 0%, transparent 40%), radial-gradient(ellipse at 90% 80%, rgba(74, 144, 164, 0.06) 0%, transparent 40%)`)
        break
      case 'linen':
        backgrounds.push(`repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px), repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px)`)
        break
    }
  }
  
  // Gradient or solid base
  if (paper.type === 'gradient') {
    backgrounds.push(`linear-gradient(${paper.gradientDirection}, ${paper.gradientFrom}, ${paper.gradientTo})`)
  }
  
  return {
    backgroundColor: baseColor,
    backgroundImage: backgrounds.length > 0 ? backgrounds.join(', ') : undefined,
    backgroundSize: paper.patternId === 'dot-grid' ? '20px 20px' : 
                    paper.patternId === 'graph' ? '20px 20px' : undefined,
  }
}

function useMobileShell(): boolean {
  const isNative = Capacitor.isNativePlatform()
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false,
  )
  useEffect(() => {
    if (isNative) return
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [isNative])
  return isNative || narrow
}

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const { isDiary, isDark, diaryThemeConfig } = useTheme()
  const mobile = useMobileShell()

  useThemeSync()

  // A person who has never told us anything about their day gets asked, once,
  // before the rail's forty destinations. Server-answered, self-limiting.
  useFirstRun()

  // Bootstrap location nudges once per authenticated session. The services
  // self-guard against non-native platforms, so this is cheap to call on web.
  useEffect(() => {
    let disposed = false
    void (async () => {
      try {
        const [{ locationNudgeService }, { geofenceSyncService }] = await Promise.all([
          import('../../services/locationNudgeService'),
          import('../../services/geofenceSyncService'),
        ])
        if (disposed) return
        await locationNudgeService.start()
        await geofenceSyncService.reconcile()
      } catch (err) {
        // Non-fatal: web platforms + devices without Play Services.
        console.debug('[geofence] bootstrap skipped:', err)
      }
    })()
    return () => { disposed = true }
  }, [])

  // Register for Web Push once per web session so the server can fan out
  // place-arrival nudges to this laptop even when the yanamas tab is closed.
  // Natively this is already covered by @capacitor/push-notifications, so
  // skip there.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return
    let disposed = false
    void (async () => {
      try {
        const { initWebPush } = await import('../../services/webPushService')
        if (disposed) return
        await initWebPush()
      } catch (err) {
        console.debug('[webpush] bootstrap skipped:', err)
      }
    })()
    return () => { disposed = true }
  }, [])

  // Compute paper background style
  const paperStyle = useMemo(() => {
    if (!isDiary) return undefined
    return getPaperBackgroundStyle(diaryThemeConfig)
  }, [isDiary, isDark, diaryThemeConfig])

  if (mobile) return (
    <HortonChatProvider>
    <FeedbackProvider>
      <MobileLayout />
    </FeedbackProvider>
    </HortonChatProvider>
  )

  return (
    <HortonChatProvider>
    <FeedbackProvider>
    <div
      className="flex h-dvh bg-default"
      style={paperStyle}
    >
      {/* Skip link: first tab stop on every screen, so a keyboard reader can
          get past the rail and the header (2026-08-29). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:inline-flex focus:min-h-touch focus:items-center focus:rounded-lg focus:bg-surface focus:px-4 focus:text-app-body focus:font-semibold focus:text-primary focus:shadow-lg focus:outline focus:outline-2 focus:outline-brand"
      >
        Skip to main content
      </a>

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* Main content */}
      {/* rail-offset mirrors the rail's own width from --rail-width, so the
          two cannot drift apart the way lg:ml-64 / lg:w-64 did. */}
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'rail-offset' : 'lg:ml-0'
      }`}>
        {/* Header */}
        <Header
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          mobileSidebarOpen={mobileSidebarOpen}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        {/* Critical reminder contracts render on EVERY screen (F5). */}
        <CriticalReminderBanner />

        {/* Page content with minimal spacing */}
        <main id="main-content" tabIndex={-1} className={`flex-1 overflow-auto page-pad ${isDiary ? 'font-diary-ui diary-page-enter' : ''}`}>
          <Outlet />
        </main>
      </div>
      <FloatingHortonButton />
    </div>
    </FeedbackProvider>
    </HortonChatProvider>
  )
}